export const API_HOST = "https://world-connect-dates.emergent.host";
export const API_BASE = `${API_HOST}/api`;
const TOKEN_KEY = "amoreska_admin_token";
const USER_KEY = "amoreska_admin_user";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};
export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
};
export const setStoredUser = (u) => localStorage.setItem(USER_KEY, JSON.stringify(u));

// Resolve a photo/selfie url that may be absolute or a relative /api/files path.
export function fileUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const token = getToken();
  const sep = path.includes("?") ? "&" : "?";
  return `${API_HOST}${path}${sep}token=${token}`;
}

async function request(path, { method = "GET", body, isLogin = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !isLogin) {
    clearToken();
    if (window.location.pathname !== "/mypersonalworkplace/login") window.location.assign("/mypersonalworkplace/login");
    throw new Error("Session expired. Please log in again.");
  }

  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const d = await res.json();
      msg = d.detail || d.message || msg;
    } catch (e) {}
    throw new Error(msg);
  }

  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export const api = {
  login: (email, password) =>
    request("/admin/login", { method: "POST", body: { email, password }, isLogin: true }),

  stats: () => request("/admin/stats"),
  trends: (days = 14) => request(`/admin/trends?days=${days}`),

  users: ({ q = "", status = "", skip = 0, limit = 25 } = {}) =>
    request(
      `/admin/users?q=${encodeURIComponent(q)}&status=${status}&skip=${skip}&limit=${limit}`
    ),
  userDetail: (uid) => request(`/admin/users/${uid}`),
  ban: (uid, reason) => request(`/admin/users/${uid}/ban`, { method: "POST", body: { reason } }),
  unban: (uid) => request(`/admin/users/${uid}/unban`, { method: "POST" }),
  warn: (uid, reason) => request(`/admin/users/${uid}/warn`, { method: "POST", body: { reason } }),
  verify: (uid, value) =>
    request(`/admin/users/${uid}/verify?value=${value}`, { method: "POST" }),
  premium: (uid, value, { plan, days } = {}) => {
    let qs = `value=${value}`;
    if (value) {
      if (plan) qs += `&plan=${plan}`;
      if (days != null && days !== "") qs += `&days=${days}`;
    }
    return request(`/admin/users/${uid}/premium?${qs}`, { method: "POST" });
  },
  removePhoto: (uid, url) =>
    request(`/admin/users/${uid}/photos/remove`, { method: "POST", body: { url } }),
  deleteUser: (uid) => request(`/admin/users/${uid}`, { method: "DELETE" }),

  reports: () => request("/admin/reports"),
  resolveReport: (rid, ban) =>
    request(`/admin/reports/${rid}/resolve?ban=${ban}`, { method: "POST" }),

  verifications: () => request("/admin/verifications"),
  reviewVerification: (vid, approve) =>
    request(`/admin/verifications/${vid}/review?approve=${approve}`, { method: "POST" }),

  audit: ({ skip = 0, limit = 50 } = {}) =>
    request(`/admin/audit?skip=${skip}&limit=${limit}`),

  demo: () => request("/admin/demo"),
  seedDemo: () => request("/admin/seed-demo", { method: "POST" }),
  wipeDemo: () => request("/admin/wipe-demo", { method: "POST" }),

  safetyScan: () => request("/admin/safety/scan", { method: "POST" }),
  safetyQueue: (resolved = false) => request(`/admin/safety-queue?resolved=${resolved}`),
  safetyAnalyze: (flagId) => request(`/admin/safety/${flagId}/analyze`, { method: "POST" }),
  safetyReview: (flagId, action) =>
    request(`/admin/safety/${flagId}/review?action=${action}`, { method: "POST" }),

  communities: () => request("/admin/communities"),
  createCommunity: (body) => request("/admin/communities", { method: "POST", body }),
  updateCommunity: (id, body) => request(`/admin/communities/${id}`, { method: "PUT", body }),
  archiveCommunity: (id) => request(`/admin/communities/${id}`, { method: "DELETE" }),

  communityPosts: (cid) => request(`/admin/communities/${cid}/posts`),
  createPost: (cid, body) => request(`/admin/communities/${cid}/posts`, { method: "POST", body }),
  deletePost: (cid, postId) =>
    request(`/admin/communities/${cid}/posts/${postId}`, { method: "DELETE" }),

  communityMixers: (cid) => request(`/admin/communities/${cid}/mixers`),
  createMixer: (cid, body) => request(`/admin/communities/${cid}/mixers`, { method: "POST", body }),
  deleteMixer: (cid, mixerId) =>
    request(`/admin/communities/${cid}/mixers/${mixerId}`, { method: "DELETE" }),

  watchRooms: (status = "active") => request(`/admin/watch/rooms?status=${status}`),
  watchRoom: (roomId) => request(`/admin/watch/rooms/${roomId}`),
  endWatchRoom: (roomId) => request(`/admin/watch/rooms/${roomId}/end`, { method: "POST" }),

  searchUsers: (q) => request(`/admin/users/search?q=${encodeURIComponent(q)}`),
  createMatch: (user_a, user_b) => request("/admin/match", { method: "POST", body: { user_a, user_b } }),
  unmatch: (matchId) => request(`/admin/match/${matchId}`, { method: "DELETE" }),

  vouches: (status) =>
    request(`/admin/vouches${status ? `?status=${status}` : ""}`),
  vouchAction: (vouchId, action) =>
    request(`/admin/vouches/${vouchId}/${action}`, { method: "POST" }),
  officialVouch: (userId, body) =>
    request(`/admin/users/${userId}/vouch`, { method: "POST", body }),
};

export const VOUCH_TAGS = [
  "Trustworthy",
  "Genuine",
  "Respectful",
  "Kind",
  "Funny",
  "Family-oriented",
  "Loyal",
  "Ambitious",
];
